import { displayToastError, http } from 'app';
import axios from 'axios';

export const getDocumentCategories = async (
  businessArea: string,
  service: string
): Promise<any> => {
  try {
    const response = await http.get(
      '/v2/:businessArea/:service/categories'
        .replace(':businessArea', businessArea)
        .replace(':service', service)
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response.data.message;
    displayToastError(String(errorMessage), 'Get Document Categories');
    throw error;
  }
};

export const uploadDocument = async (
  businessArea: string,
  service: string,
  file: File
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('document', file);

    const response = await http.post(
      '/v2/:businessArea/:service'
        .replace(':businessArea', businessArea)
        .replace(':service', service),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response.data.message;
    displayToastError(String(errorMessage), 'Upload Document');
    throw error;
  }
};

export const getDocumentsByUser = async (
  businessArea: string,
  service: string,
  query?: {
    limit?: number;
    offset?: number;
    search?: string;
    filter?: string;
  }
): Promise<any> => {
  try {
    const queryParams = new URLSearchParams();
    if (query?.limit) queryParams.append('limit', query.limit.toString());
    if (query?.offset) queryParams.append('offset', query.offset.toString());
    if (query?.search) queryParams.append('search', query.search);
    if (query?.filter) queryParams.append('filter', query.filter);

    const queryString = queryParams.toString();
    const url = `/v2/:businessArea/:service${
      queryString ? `?${queryString}` : ''
    }`
      .replace(':businessArea', businessArea)
      .replace(':service', service);

    const response = await http.get(url);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response.data.message;
    displayToastError(String(errorMessage), 'Get Documents by User');
    throw error;
  }
};

export const getSasTokenForRead = async (
  businessArea: string,
  service: string,
  documentUrl: string
): Promise<any> => {
  try {
    const encodedUrl = encodeURIComponent(documentUrl);
    const response = await http.get(
      `/v2/:businessArea/:service/sas-token?documentUrl=${encodedUrl}`
        .replace(':businessArea', businessArea)
        .replace(':service', service)
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response.data.message;
    displayToastError(String(errorMessage), 'Get SAS Token');
    throw error;
  }
};

export const getDocumentViaProxy = async (
  businessArea: string,
  service: string,
  documentUrl: string
): Promise<Blob> => {
  try {
    const encodedUrl = encodeURIComponent(documentUrl);
    const response = await http.get<Blob>(
      `/v2/:businessArea/:service/document-proxy?documentUrl=${encodedUrl}`
        .replace(':businessArea', businessArea)
        .replace(':service', service),
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || 'Failed to fetch document';
    displayToastError(String(errorMessage), 'Get Document');
    throw error;
  }
};

export const createComparison = async (
  businessArea: string,
  service: string,
  payload: {
    name: string;
    document1Id: string;
    document2Id: string;
  }
): Promise<any> => {
  try {
    const response = await http.post(
      '/v2/:businessArea/:service/comparison'
        .replace(':businessArea', businessArea)
        .replace(':service', service),
      payload
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response.data.message;
    displayToastError(String(errorMessage), 'Create Comparison');
    throw error;
  }
};

export const getComparisonReport = async (
  businessArea: string,
  service: string,
  comparisonId: string
): Promise<any> => {
  try {
    const response = await http.get(
      `/v2/:businessArea/:service/comparison/${comparisonId}`
        .replace(':businessArea', businessArea)
        .replace(':service', service)
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response.data.message;
    displayToastError(String(errorMessage), 'Get Comparison Report');
    throw error;
  }
};

export const getComparisonHistory = async (
  businessArea: string,
  service: string,
  query?: {
    limit?: number;
    offset?: number;
    search?: string;
    sort?: 'upload_date' | 'name';
    direction?: 0 | 1;
  }
): Promise<any> => {
  try {
    const queryParams = new URLSearchParams();
    if (query?.limit) queryParams.append('limit', query.limit.toString());
    if (query?.offset) queryParams.append('offset', query.offset.toString());
    if (query?.search) queryParams.append('search', query.search);
    if (query?.sort) queryParams.append('sort', query.sort);
    if (query?.direction !== undefined)
      queryParams.append('direction', query.direction.toString());

    const queryString = queryParams.toString();
    const url = `/v2/:businessArea/:service/comparison/history${
      queryString ? `?${queryString}` : ''
    }`
      .replace(':businessArea', businessArea)
      .replace(':service', service);

    const response = await http.get(url);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response.data.message;
    displayToastError(String(errorMessage), 'Get Comparison History');
    throw error;
  }
};

export const deleteComparison = async (
  businessArea: string,
  service: string,
  comparisonId: string
): Promise<any> => {
  try {
    const response = await http.delete(
      `/v2/:businessArea/:service/comparison/${comparisonId}`
        .replace(':businessArea', businessArea)
        .replace(':service', service)
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response.data.message;
    displayToastError(String(errorMessage), 'Delete Comparison');
    throw error;
  }
};

export const getComparisonStatusStream = async (
  businessArea: string,
  service: string,
  comparisonId: string
): Promise<ReadableStreamDefaultReader<Uint8Array>> => {
  const relativePath =
    `/v2/:businessArea/:service/comparison/${comparisonId}/status`
      .replace(':businessArea', businessArea)
      .replace(':service', service);

  let processedHeaders: Record<string, string> = {};
  try {
    await axios.request({
      url: relativePath,
      method: 'GET',
      adapter: async (config: any) => {
        const hdrs: Record<string, string> = {};
        const rawHeaders = config.headers?.toJSON
          ? config.headers.toJSON()
          : config.headers ?? {};
        for (const [key, value] of Object.entries(rawHeaders)) {
          if (typeof value === 'string' && value) {
            hdrs[key] = value;
          }
        }
        processedHeaders = hdrs;
        return await Promise.reject(new Error('headers extracted'));
      }
    });
  } catch {
    // Expected: adapter rejects after extracting headers
  }

  const baseURL = axios.defaults.baseURL ?? '';
  const fullUrl = `${baseURL}${relativePath}`;

  const response = await fetch(fullUrl, {
    method: 'GET',
    headers: processedHeaders
  });

  if (!response.body) {
    throw new Error('No response body');
  }

  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }

  return response.body.getReader();
};
